/**
 * TrustScoreTrend - Line chart showing Trust Score trends over time
 */
import React, { useState, useEffect } from 'react';
import { Card, Spin, Empty } from 'antd';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { getScanHistory } from '../api/trustgridApi';
import { showError } from '../../../utils/notifications';

function TrustScoreTrend({ projectId }) {
  const [scanHistory, setScanHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (projectId) {
      fetchScanHistory();
    }
  }, [projectId]);

  const fetchScanHistory = async () => {
    setLoading(true);
    try {
      const response = await getScanHistory(projectId, 1, 20);
      // Reverse to show oldest first (left to right)
      const scans = (response.data.scans || []).reverse();
      setScanHistory(scans);
    } catch (error) {
      console.error('Failed to fetch scan history:', error);
      showError(error);
    } finally {
      setLoading(false);
    }
  };

  // Format data for recharts
  const chartData = scanHistory.map((scan, index) => ({
    name: `Scan ${scan.id}`,
    scanId: scan.id,
    date: new Date(scan.started_at).toLocaleDateString(),
    trustScore: scan.trust_score || 0,
    // Add framework scores as separate lines
    ...(scan.framework_scores || {})
  }));

  // Extract framework names for legend
  const frameworkNames = scanHistory.length > 0
    ? Object.keys(scanHistory[0].framework_scores || {})
    : [];

  const COLORS = {
    trustScore: '#1890ff',
    'GDPR': '#52c41a',
    'EU AI Act': '#faad14',
    'ISO 42001': '#722ed1',
    'SOC 2': '#eb2f96',
    'NIST AI RMF': '#13c2c2',
    'HIPAA': '#fa8c16'
  };

  if (loading) {
    return (
      <Card>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  if (scanHistory.length === 0) {
    return (
      <Card>
        <Empty
          description="No scan history available"
          style={{ padding: '40px' }}
        />
      </Card>
    );
  }

  if (scanHistory.length === 1) {
    return (
      <Card>
        <Empty
          description="Need at least 2 scans to show trends"
          style={{ padding: '40px' }}
        />
      </Card>
    );
  }

  return (
    <Card>
      <h3>Trust Score Trend</h3>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="scanId"
            label={{ value: 'Scan ID', position: 'insideBottom', offset: -5 }}
            tick={{ fontSize: 12 }}
          />
          <YAxis domain={[0, 100]} />
          <Tooltip />
          <Legend />
          <Line
            type="monotone"
            dataKey="trustScore"
            stroke={COLORS.trustScore}
            strokeWidth={3}
            name="Trust Score"
            dot={{ r: 5 }}
          />
          {frameworkNames.map(framework => (
            <Line
              key={framework}
              type="monotone"
              dataKey={framework}
              stroke={COLORS[framework] || '#888'}
              strokeWidth={2}
              name={framework}
              dot={{ r: 3 }}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </Card>
  );
}

export default TrustScoreTrend;
